import type { Action, Condition, Workflow } from '@/api/workflows'

/** What starts a workflow. */
export const TRIGGERS = [
  { value: 'deal_created', label: 'A deal is created' },
  { value: 'deal_stage_changed', label: 'A deal moves to a stage' },
  { value: 'date_reached', label: 'A deal date arrives' },
  { value: 'contact_date_reached', label: 'A contact date comes round (birthday etc.)' },
]

export const CONDITION_FIELDS = [
  { value: 'value', label: 'Deal value' },
  { value: 'status', label: 'Deal status' },
  { value: 'stageId', label: 'Stage' },
  { value: 'title', label: 'Deal name' },
]

export const CONDITION_OPS = [
  { value: 'eq', label: 'is' },
  { value: 'ne', label: 'is not' },
  { value: 'gte', label: 'is at least' },
  { value: 'lte', label: 'is at most' },
  { value: 'gt', label: 'is more than' },
  { value: 'lt', label: 'is less than' },
]

/** Every step a workflow can take; some don't apply to contact-date workflows. */
export const ACTION_TYPES = [
  { value: 'send_email', label: 'Send an email to the client', contact: true },
  { value: 'send_sms', label: 'Send an SMS to the client', contact: true },
  { value: 'notify', label: 'Notify a teammate', contact: true },
  { value: 'create_task', label: 'Create a task', contact: true },
  { value: 'add_note', label: 'Add a note to the timeline', contact: true },
  // kept for existing workflows; it now plans a follow-up task (the next contact date follows tasks)
  { value: 'set_next_contact_date', label: 'Plan a follow-up (next contact)', contact: false },
] as const

/** A fresh step of the given type with sensible defaults. */
export function newAction(type: string): Action {
  switch (type) {
    case 'send_email':
    case 'send_sms':
      return { type, templateId: '' }
    case 'notify':
      return { type, to: 'owner', message: '' }
    case 'create_task':
      return { type, title: '', dueInDays: 1, assignee: 'owner' }
    case 'add_note':
      return { type, body: '' }
    default:
      return { type, inDays: 7 }
  }
}

export interface Lookups {
  stageName: (id: unknown) => string
  templateName: (id: unknown) => string
  memberName: (id: unknown) => string
  dateFieldLabel: (key: string) => string
}

/** "When a deal moves to Won Business", in words. */
export function describeTrigger(w: Pick<Workflow, 'triggerType' | 'triggerConfig'>, l: Lookups): string {
  const cfg = w.triggerConfig ?? {}
  switch (w.triggerType) {
    case 'deal_created':
      return 'When a deal is created'
    case 'deal_stage_changed':
      return cfg.stageId ? `When a deal moves to ${l.stageName(cfg.stageId)}` : 'When a deal changes stage'
    case 'date_reached':
    case 'contact_date_reached': {
      const offset = Number(cfg.offsetDays ?? 0)
      const yearly = cfg.repeatYearly === true || cfg.repeatYearly === 'true'
      const field = l.dateFieldLabel(String(cfg.dateField ?? ''))
      const whose = w.triggerType === 'contact_date_reached' ? "a contact's" : "a deal's"
      const when = offset ? `${offset} day${offset === 1 ? '' : 's'} after ${whose} ${field}` : `On ${whose} ${field}`
      return `${when}${yearly ? ', every year' : ''}`
    }
    default:
      return w.triggerType
  }
}

export function describeCondition(c: Condition, l: Lookups): string {
  const field = CONDITION_FIELDS.find((f) => f.value === c.field)?.label ?? c.field
  const op = CONDITION_OPS.find((o) => o.value === c.op)?.label ?? c.op
  const value =
    c.field === 'stageId' ? l.stageName(c.value) : c.field === 'value' ? `$${Number(c.value ?? 0).toLocaleString()}` : String(c.value ?? '')
  return `${field} ${op} ${value}`
}

/** One step, e.g. "Email the client “Just Sold Announcement”". */
export function describeAction(a: Action, l: Lookups): string {
  const who = (to: unknown) => (!to || to === 'owner' ? 'the owner' : l.memberName(to))
  switch (a.type) {
    case 'send_email':
      return a.templateId ? `Email the client “${l.templateName(a.templateId)}”` : `Email the client “${String(a.subject || 'custom email')}”`
    case 'send_sms':
      return a.templateId ? `Text the client “${l.templateName(a.templateId)}”` : 'Text the client a custom SMS'
    case 'notify':
      return `Notify ${who(a.to)}`
    case 'create_task':
      return `Task for ${who(a.assignee)}: “${String(a.title || 'Follow up')}”${dueText(a.dueInDays)}`
    case 'add_note':
      return 'Add a note to the timeline'
    case 'set_next_contact_date':
      return `Plan a follow-up task ${Number(a.inDays ?? 0)} days ahead`
    default:
      return a.type
  }
}

function dueText(days: unknown): string {
  if (days == null || days === '') return ''
  const n = Number(days)
  return n === 0 ? ', due the same day' : `, due in ${n} day${n === 1 ? '' : 's'}`
}

export function delayOf(a: Action): number {
  return Number(a.delayDays ?? 0)
}
